import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLabelingMailbox } from '@/api/rpc/hooks/ship-and-debit/useLabeling';
import type { LabelingMailboxFilter } from '@erp/rpc';
import { LabelingBrowseTable } from './LabelingBrowseTable';
import { LabelingEmailSheet } from './LabelingEmailSheet';

const PAGE_SIZE = 25;

const FILTERS: LabelingMailboxFilter[] = ['unlabeled', 'labeled', 'all'];

interface Props {
  filter: LabelingMailboxFilter;
  onFilterChange: (filter: LabelingMailboxFilter) => void;
  search: string;
  onSearchChange: (search: string) => void;
  openEmailId: string | null;
  onOpenEmail: (inboundEmailId: string | null) => void;
  /** The user can work the review page, so the sheet offers to open the email there. */
  canReview: boolean;
}

/**
 * Browse mode — the whole mailbox as a list, with the detail view as a slide-over.
 *
 * Owns its own paging state but takes filter/search/open-email from the page, which
 * keeps them in the URL. Split from LabelingPage so neither component has to hold
 * both modes' state at once.
 */
export function LabelingBrowseMode({
  filter,
  onFilterChange,
  search,
  onSearchChange,
  openEmailId,
  onOpenEmail,
  canReview,
}: Props) {
  const gt = useGT();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useLabelingMailbox({
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    filter,
  });

  function changeFilter(next: LabelingMailboxFilter) {
    setPage(1);
    onFilterChange(next);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={filter}
          onValueChange={(v) => changeFilter(v as LabelingMailboxFilter)}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTERS.map((f) => (
              <SelectItem key={f} value={f}>
                {filterLabel(f, gt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={search}
          onChange={(e) => {
            setPage(1);
            onSearchChange(e.target.value);
          }}
          placeholder={gt('Search sender, subject or body…', {
            context: 'labeling browse search placeholder',
          })}
          className="max-w-sm"
        />

        {data ? (
          <span className="ml-auto text-sm text-muted-foreground">
            {data.totalCount} <T context="labeling browse total">emails</T>
          </span>
        ) : null}
      </div>

      <LabelingBrowseTable
        items={data?.items ?? []}
        isLoading={isLoading}
        onOpen={onOpenEmail}
      />

      {data && data.totalPages > 1 ? (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <T context="labeling browse pagination">Previous</T>
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <T context="labeling browse pagination">Next</T>
          </Button>
        </div>
      ) : null}

      <LabelingEmailSheet
        inboundEmailId={openEmailId}
        onClose={() => onOpenEmail(null)}
        canReview={canReview}
      />
    </div>
  );
}

function filterLabel(
  filter: LabelingMailboxFilter,
  gt: (s: string, o?: { context?: string }) => string,
): string {
  const ctx = { context: 'labeling browse filter' };
  if (filter === 'unlabeled') return gt('Unlabeled', ctx);
  if (filter === 'labeled') return gt('Labeled', ctx);
  return gt('All emails', ctx);
}
